export const WEBP_TO_GIF_LIMITS = Object.freeze({
  bytes: 25 * 1024 * 1024,
  canvasPixels: 16_000_000,
  frames: 400,
  decodedFramePixels: 250_000_000
});

const textDecoder = new TextDecoder("ascii");

function fourCC(bytes, offset) {
  return textDecoder.decode(bytes.subarray(offset, offset + 4));
}

function uint16(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function uint24(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16);
}

function uint32(bytes, offset) {
  return (bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24)) >>> 0;
}

function writeUint24(bytes, offset, value) {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
  bytes[offset + 2] = (value >>> 16) & 0xff;
}

function writeUint32(bytes, offset, value) {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
  bytes[offset + 2] = (value >>> 16) & 0xff;
  bytes[offset + 3] = (value >>> 24) & 0xff;
}

function writeFourCC(bytes, offset, value) {
  for (let index = 0; index < 4; index += 1) bytes[offset + index] = value.charCodeAt(index);
}

function chunks(bytes, start, end) {
  const result = [];
  let offset = start;
  while (offset < end) {
    if (offset + 8 > end) throw new Error("The WebP file contains a truncated chunk header.");
    const type = fourCC(bytes, offset);
    const size = uint32(bytes, offset + 4);
    const payloadStart = offset + 8;
    const payloadEnd = payloadStart + size;
    const paddedEnd = payloadEnd + (size & 1);
    if (payloadEnd > end || paddedEnd > end) throw new Error(`The ${type.trim() || "unknown"} WebP chunk is truncated.`);
    result.push({ type, size, offset, payloadStart, payloadEnd, paddedEnd });
    offset = paddedEnd;
  }
  return result;
}

function stillDimensions(bytes, imageChunk, extendedDimensions) {
  if (extendedDimensions) return extendedDimensions;
  if (!imageChunk) throw new Error("The WebP file does not contain decodable image data.");
  const offset = imageChunk.payloadStart;
  if (imageChunk.type === "VP8 ") {
    if (imageChunk.size < 10 || bytes[offset + 3] !== 0x9d || bytes[offset + 4] !== 0x01 || bytes[offset + 5] !== 0x2a)
      throw new Error("The lossy WebP frame header is invalid.");
    return {
      width: uint16(bytes, offset + 6) & 0x3fff,
      height: uint16(bytes, offset + 8) & 0x3fff
    };
  }
  if (imageChunk.type === "VP8L") {
    if (imageChunk.size < 5 || bytes[offset] !== 0x2f) throw new Error("The lossless WebP frame header is invalid.");
    return {
      width: 1 + bytes[offset + 1] + ((bytes[offset + 2] & 0x3f) << 8),
      height: 1 + (bytes[offset + 2] >>> 6) + (bytes[offset + 3] << 2) + ((bytes[offset + 4] & 0x0f) << 10)
    };
  }
  throw new Error("The WebP image encoding is not supported.");
}

export function hasWebPHeader(bytes) {
  return bytes instanceof Uint8Array &&
    bytes.length >= 12 &&
    fourCC(bytes, 0) === "RIFF" &&
    fourCC(bytes, 8) === "WEBP";
}

export function parseWebP(bytes) {
  if (!hasWebPHeader(bytes)) throw new Error("File content is not a valid WebP image.");
  const declaredEnd = uint32(bytes, 4) + 8;
  if (declaredEnd < 20 || declaredEnd > bytes.length) throw new Error("The WebP RIFF container is truncated.");

  const topLevel = chunks(bytes, 12, declaredEnd);
  const vp8x = topLevel.find(chunk => chunk.type === "VP8X");
  let extendedDimensions = null;
  let animationFlag = false;
  if (vp8x) {
    if (vp8x.size < 10) throw new Error("The extended WebP header is truncated.");
    animationFlag = Boolean(bytes[vp8x.payloadStart] & 0x02);
    extendedDimensions = {
      width: uint24(bytes, vp8x.payloadStart + 4) + 1,
      height: uint24(bytes, vp8x.payloadStart + 7) + 1
    };
  }

  const animation = topLevel.find(chunk => chunk.type === "ANIM");
  const frameChunks = topLevel.filter(chunk => chunk.type === "ANMF");
  if (animationFlag || animation || frameChunks.length) {
    if (!vp8x || !animation || animation.size < 6 || !frameChunks.length)
      throw new Error("The animated WebP container is incomplete.");

    const backgroundOffset = animation.payloadStart;
    const frames = frameChunks.map((chunk, index) => {
      if (chunk.size < 16) throw new Error(`Animated WebP frame ${index + 1} is truncated.`);
      const offset = chunk.payloadStart;
      const frame = {
        x: uint24(bytes, offset) * 2,
        y: uint24(bytes, offset + 3) * 2,
        width: uint24(bytes, offset + 6) + 1,
        height: uint24(bytes, offset + 9) + 1,
        duration: uint24(bytes, offset + 12),
        blend: (bytes[offset + 15] & 0x02) === 0,
        dispose: Boolean(bytes[offset + 15] & 0x01),
        dataStart: offset + 16,
        dataEnd: chunk.payloadEnd
      };
      if (frame.x + frame.width > extendedDimensions.width || frame.y + frame.height > extendedDimensions.height)
        throw new Error(`Animated WebP frame ${index + 1} exceeds the image canvas.`);
      const frameParts = chunks(bytes, frame.dataStart, frame.dataEnd);
      if (!frameParts.some(part => part.type === "VP8 " || part.type === "VP8L"))
        throw new Error(`Animated WebP frame ${index + 1} has no image bitstream.`);
      return frame;
    });

    return {
      animated: true,
      width: extendedDimensions.width,
      height: extendedDimensions.height,
      loopCount: uint16(bytes, animation.payloadStart + 4),
      background: [
        bytes[backgroundOffset + 2],
        bytes[backgroundOffset + 1],
        bytes[backgroundOffset],
        bytes[backgroundOffset + 3]
      ],
      frames,
      profile: topLevel.find(chunk => chunk.type === "ICCP") ?? null
    };
  }

  const imageChunk = topLevel.find(chunk => chunk.type === "VP8 " || chunk.type === "VP8L");
  const dimensions = stillDimensions(bytes, imageChunk, extendedDimensions);
  return {
    animated: false,
    width: dimensions.width,
    height: dimensions.height,
    loopCount: 1,
    background: [0, 0, 0, 0],
    frames: [{ x: 0, y: 0, width: dimensions.width, height: dimensions.height, duration: 1000, blend: false, dispose: false }],
    profile: topLevel.find(chunk => chunk.type === "ICCP") ?? null
  };
}

export function validateWebPForGif(metadata, byteLength) {
  if (byteLength > WEBP_TO_GIF_LIMITS.bytes) throw new Error("WebP files are limited to 25 MB.");
  if (!metadata.width || !metadata.height) throw new Error("The WebP image has invalid dimensions.");
  if (metadata.width > 65_535 || metadata.height > 65_535)
    throw new Error("GIF output supports dimensions up to 65,535 pixels.");
  const canvasPixels = metadata.width * metadata.height;
  if (canvasPixels > WEBP_TO_GIF_LIMITS.canvasPixels)
    throw new Error("The WebP canvas exceeds the 16 megapixel limit.");
  if (metadata.frames.length > WEBP_TO_GIF_LIMITS.frames)
    throw new Error(`Animated WebP files are limited to ${WEBP_TO_GIF_LIMITS.frames} frames.`);
  if (canvasPixels * metadata.frames.length > WEBP_TO_GIF_LIMITS.decodedFramePixels)
    throw new Error("The animation is too large to convert safely. Reduce its dimensions or frame count.");
  return metadata;
}

export function normalizeGifDelay(milliseconds) {
  const value = Number.isFinite(milliseconds) ? milliseconds : 100;
  return Math.max(20, Math.min(655_350, Math.round(value / 10) * 10));
}

export function gifRepeatCount(webpLoopCount) {
  if (!Number.isInteger(webpLoopCount) || webpLoopCount < 0) return 0;
  return webpLoopCount === 0 ? 0 : webpLoopCount - 1 || -1;
}

function copyChunk(target, targetOffset, source, chunk) {
  target.set(source.subarray(chunk.offset, chunk.paddedEnd), targetOffset);
  return targetOffset + (chunk.paddedEnd - chunk.offset);
}

export function webPFrameBytes(source, metadata, frame) {
  if (!metadata.animated) return source.slice();
  const frameParts = chunks(source, frame.dataStart, frame.dataEnd);
  const image = frameParts.find(part => part.type === "VP8 " || part.type === "VP8L");
  const alpha = frameParts.find(part => part.type === "ALPH");
  if (!image) throw new Error("The WebP animation frame has no image data.");

  const needsExtendedHeader = Boolean(alpha || metadata.profile);
  const selected = [metadata.profile, alpha, image].filter(Boolean);
  const extendedLength = needsExtendedHeader ? 18 : 0;
  const chunkLength = selected.reduce((total, chunk) => total + chunk.paddedEnd - chunk.offset, 0);
  const result = new Uint8Array(12 + extendedLength + chunkLength);
  writeFourCC(result, 0, "RIFF");
  writeUint32(result, 4, result.length - 8);
  writeFourCC(result, 8, "WEBP");
  let offset = 12;

  if (needsExtendedHeader) {
    writeFourCC(result, offset, "VP8X");
    writeUint32(result, offset + 4, 10);
    result[offset + 8] = (metadata.profile ? 0x20 : 0) | (alpha ? 0x10 : 0);
    writeUint24(result, offset + 12, frame.width - 1);
    writeUint24(result, offset + 15, frame.height - 1);
    offset += 18;
  }
  for (const chunk of selected) offset = copyChunk(result, offset, source, chunk);
  return result;
}

export function webPDescription(metadata) {
  return metadata.animated
    ? `${metadata.frames.length} frames · ${metadata.width} × ${metadata.height} px`
    : `Static image · ${metadata.width} × ${metadata.height} px`;
}
